import { memo } from "react";
import * as Styled from "./style";
import { PlayButton } from "~/components/blocks/common/play-button";
import type { VideoLegacySeasonType } from "~/components/blocks/product/type-guard";
import { useHandleClickByDevice } from "~/hooks/use-handle-click-by-device";
import type { G_VideoPpvExpiration, Maybe } from "~/lib/graphql";
import { G_VideoContentType } from "~/lib/graphql";

type PlayIconButtonProps = {
  content: VideoLegacySeasonType["content"];
  ppvExpiration: Maybe<G_VideoPpvExpiration> | undefined;
  ppvProducts: VideoLegacySeasonType["content"]["ppvProducts"];
  svodProduct: VideoLegacySeasonType["content"]["svodProduct"];
  continueWatching: VideoLegacySeasonType["continueWatching"];
  isStreamable: boolean;
  isPlayBtnDisplay: boolean;
  onPlayBtnPressed: () => void;
};

/** VideoLegacySeason の再生アイコンの出し分けに使用する */
export const PlayIconButton = memo((props: PlayIconButtonProps) => {
  const {
    content,
    ppvExpiration,
    ppvProducts,
    svodProduct,
    continueWatching,
    isStreamable,
    isPlayBtnDisplay,
    onPlayBtnPressed,
  } = props;

  const { handleClickByDevice } = useHandleClickByDevice();

  // 続きから見るがある場合
  if (continueWatching !== null && continueWatching !== undefined) {
    const {
      resumePoint,
      partNumber,
      content: {
        playInfo: { parts },
        viewingRights: { isStreamable: isContinueStreamable },
      },
    } = continueWatching;

    // MEMO（kimura）: isContinueStreamable が true の場合の処理
    if (isContinueStreamable) {
      if (resumePoint > 0) {
        if (parts.length > 1) {
          return (
            <Styled.DivPlayPositionCenter isPlayBtnDisplay={isPlayBtnDisplay}>
              <PlayButton {...handleClickByDevice(onPlayBtnPressed)} />
              <Styled.ParText>パート{partNumber}を続きから再生</Styled.ParText>
            </Styled.DivPlayPositionCenter>
          );
        } else {
          return (
            <Styled.DivPlayPositionCenter isPlayBtnDisplay={isPlayBtnDisplay}>
              <PlayButton {...handleClickByDevice(onPlayBtnPressed)} />
              <Styled.ParText>続きから再生</Styled.ParText>
            </Styled.DivPlayPositionCenter>
          );
        }
      } else {
        if (parts.length > 1) {
          return (
            <Styled.DivPlayPositionCenter isPlayBtnDisplay={isPlayBtnDisplay}>
              <PlayButton {...handleClickByDevice(onPlayBtnPressed)} />
              <Styled.ParText>パート{partNumber}を再生</Styled.ParText>
            </Styled.DivPlayPositionCenter>
          );
        } else {
          return (
            <Styled.DivPlayPositionCenter isPlayBtnDisplay={isPlayBtnDisplay}>
              <PlayButton {...handleClickByDevice(onPlayBtnPressed)} />
              <Styled.ParText>再生</Styled.ParText>
            </Styled.DivPlayPositionCenter>
          );
        }
      }
    } else {
      // MEMO（kimura）: isContinueStreamable が false の場合の処理

      if (ppvProducts.length > 0) {
        return (
          <Styled.DivPlayPositionCenter isPlayBtnDisplay={isPlayBtnDisplay}>
            <PlayButton {...handleClickByDevice(onPlayBtnPressed)} />
            <Styled.ParText>購入</Styled.ParText>
          </Styled.DivPlayPositionCenter>
        );
      }

      if (svodProduct !== null && svodProduct !== undefined) {
        return (
          <Styled.DivPlayPositionCenter isPlayBtnDisplay={isPlayBtnDisplay}>
            <PlayButton {...handleClickByDevice(onPlayBtnPressed)} />
            <Styled.ParText>再生</Styled.ParText>
          </Styled.DivPlayPositionCenter>
        );
      }

      return null;
    }
  } else {
    const {
      contentType,
      playInfo: { parts },
    } = content;

    if (contentType === G_VideoContentType.Vod_2D) {
      if (isStreamable) {
        if (parts.length > 1) {
          return (
            <Styled.DivPlayPositionCenter isPlayBtnDisplay={isPlayBtnDisplay}>
              <PlayButton {...handleClickByDevice(onPlayBtnPressed)} />
              <Styled.ParText>パート{parts[0].number}を再生</Styled.ParText>
            </Styled.DivPlayPositionCenter>
          );
        } else {
          return (
            <Styled.DivPlayPositionCenter isPlayBtnDisplay={isPlayBtnDisplay}>
              <PlayButton {...handleClickByDevice(onPlayBtnPressed)} />
              <Styled.ParText>再生</Styled.ParText>
            </Styled.DivPlayPositionCenter>
          );
        }
      } else {
        if (ppvProducts.length > 0) {
          return (
            <Styled.DivPlayPositionCenter isPlayBtnDisplay={isPlayBtnDisplay}>
              <PlayButton {...handleClickByDevice(onPlayBtnPressed)} />
              <Styled.ParText>購入</Styled.ParText>
            </Styled.DivPlayPositionCenter>
          );
        }

        if (svodProduct !== null && svodProduct !== undefined) {
          return (
            <Styled.DivPlayPositionCenter isPlayBtnDisplay={isPlayBtnDisplay}>
              <PlayButton {...handleClickByDevice(onPlayBtnPressed)} />
              <Styled.ParText>再生</Styled.ParText>
            </Styled.DivPlayPositionCenter>
          );
        }

        return null;
      }
    }

    if (contentType === G_VideoContentType.VodVr) {
      // MEMO : VRかつ購入済みの場合、ブラウザ再生できないので再生ボタンは表示しない
      if (ppvExpiration !== null && ppvExpiration !== undefined) {
        return null;
      }

      if (ppvProducts.length > 0) {
        return (
          <Styled.DivPlayPositionCenter isPlayBtnDisplay={isPlayBtnDisplay}>
            <PlayButton {...handleClickByDevice(onPlayBtnPressed)} />
            <Styled.ParText>購入</Styled.ParText>
          </Styled.DivPlayPositionCenter>
        );
      } else {
        return null;
      }
    }
  }

  return null;
});

PlayIconButton.displayName = "PlayIconButton";
